import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import axios from "axios";

import PawnSchemeManagement from "./PawnSchemeManagement";
import PawnAmountSlabs, {
  emptySlabRow,
  validateSlabRows,
  slabsToRows,
  rowsToSlabs,
} from "./PawnAmountSlabs";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

/**
 * The Interest Slab section of the pawn settings screen — one section holding
 * BOTH interest bases, with a switch between them.
 *
 * ── Why one section and not two ─────────────────────────────────────────────
 * A store prices its loans on exactly one basis: either month-wise (a scheme's
 * rate, overridden by the month band a receipt falls in) or amount-wise (the
 * rate is decided by the size of the loan). Never both — a quoted rate has to
 * have one unambiguous source. Two side-by-side sections would show every store
 * an editor it does not use and invite someone to fill in both.
 *
 * ── Why this section saves itself ───────────────────────────────────────────
 * The page's own Save writes store_pawn_settings. The basis and its ladder are
 * one decision and are written together through /amount-slabs (see
 * amountSlabController) — so if this section rode on the page's Save, a store
 * could end up switched to amount-wise with no ladder, or with a ladder it is
 * not using. Instead the section keeps its edits local and shows its own action
 * bar the moment something is unsaved, which also keeps a second "Save" button
 * off the screen until it means something.
 */

const BLUE = "#2563EB";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const PURPLE = "#9333EA";
const AMBER_BG = "#FFFBEB";
const AMBER_LINE = "#FDE68A";
const AMBER_INK = "#92400E";

const CARD_RADIUS = "16px";

const BASIS_INFO = {
  title: "Month-wise or amount-wise interest",
  description:
    "A store charges interest on one of two bases, and this switch says which one. Month-wise is the Scheme Master: each scheme carries a rate, and optionally a different rate for each month band of the loan — up to 1 month, 1 to 3 months, 3 to 12 months, and after 12 months — so the rate depends on how long the loan has run. Amount-wise ignores schemes and months entirely: the rate is decided by how big the loan is, from a ladder of amount bands you set up here, and the payment screen fills the interest in as soon as the loan amount is typed. A store uses one basis or the other, never both. Switching affects NEW loans only — every existing loan keeps the rate it was booked at.",
};

/** Stable shape for the dirty check — order matters, formatting does not. */
const rowsFingerprint = (rows) =>
  JSON.stringify(
    (rows || []).map((r) => [
      r.from_amount === "" ? null : Number(r.from_amount),
      r.to_amount === "" ? null : Number(r.to_amount),
      r.interest === "" ? null : Number(r.interest),
    ]),
  );

const PawnInterestSlabs = ({ storeId, currentStatus = 1, userId = null }) => {
  const [basis, setBasis] = useState("month");
  const [rows, setRows] = useState([]);
  // What the server currently holds, so "unsaved" is a fact rather than a guess.
  const [saved, setSaved] = useState({ basis: "month", rows: [] });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rowErrors, setRowErrors] = useState({});
  const [infoOpen, setInfoOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchLadder = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/amount-slabs/${storeId}`,
      );
      if (res.data.success && res.data.data) {
        const nextBasis =
          res.data.data.interest_slab_mode === "amount" ? "amount" : "month";
        const nextRows = slabsToRows(res.data.data.slabs);
        setBasis(nextBasis);
        // A store already ON the amount basis but with no bands stored yet has
        // to land on something typeable. Without this it loaded an empty table
        // with nothing to fill in, and the only way to get a row was to toggle
        // away to month-wise and back.
        const seeded =
          nextBasis === "amount" && nextRows.length === 0
            ? [emptySlabRow()]
            : nextRows;
        setRows(seeded);
        // Baseline is what we actually put on screen, so an untouched blank row
        // does not announce itself as an unsaved change the moment the page
        // opens — the action bar appears when someone types, and "Discard"
        // returns to the blank row rather than to no row at all.
        setSaved({ basis: nextBasis, rows: seeded });
        setRowErrors({});
      } else {
        showSnackbar(
          res.data.statusMessage || "Failed to load the interest basis",
          "error",
        );
      }
    } catch (error) {
      showSnackbar("Failed to load the interest basis", "error");
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchLadder();
  }, [fetchLadder]);

  const basisChanged = basis !== saved.basis;
  const ladderChanged =
    basis === "amount" && rowsFingerprint(rows) !== rowsFingerprint(saved.rows);
  const dirty = basisChanged || ladderChanged;

  /**
   * Rows changed. Any field the user has just corrected must stop being red —
   * an error that outlives its cause reads as "still wrong" and sends people
   * hunting for a problem they already fixed.
   *
   * Only rows that were ALREADY flagged are re-checked: re-validating the whole
   * ladder on every keystroke would light up the blank row that "Add slab" just
   * created, before the user has had a chance to type in it.
   */
  const handleRowsChange = (next) => {
    setRows(next);
    setRowErrors((prev) => {
      const flagged = Object.keys(prev);
      if (flagged.length === 0) return prev;
      const fresh = validateSlabRows(next);
      const kept = {};
      for (const key of flagged) if (fresh[key]) kept[key] = fresh[key];
      return kept;
    });
  };

  const handleBasisChange = (_, next) => {
    // ToggleButtonGroup hands back null when the selected button is clicked
    // again. A store must be on some basis, so that click is a no-op.
    if (!next || next === basis) return;
    setRowErrors({});
    setBasis(next);
    // Land on something editable rather than an empty table: one blank band.
    // The load path seeds the same row, for a store that is already on this
    // basis with nothing stored.
    if (next === "amount" && rows.length === 0) setRows([emptySlabRow()]);
  };

  const handleSave = async () => {
    // Block the save and mark the offending fields ourselves — the API's
    // "slab 2: interest % must be greater than 0" is not the user's feedback.
    if (basis === "amount") {
      const errors = validateSlabRows(rows);
      if (Object.keys(errors).length > 0) {
        setRowErrors(errors);
        showSnackbar(
          "Check the highlighted slabs — each one needs a From, a rate above 0, and a To that is not below its From",
          "error",
        );
        return;
      }
    }
    setRowErrors({});
    setSaving(true);
    try {
      const payload = {
        interest_slab_mode: basis,
        // Only sent on the amount basis. Switching BACK to month-wise must not
        // push a half-typed ladder at the validator — and leaving `slabs` out
        // tells the backend to keep whatever ladder is stored, so the store can
        // switch back to it later.
        // Each row carries the top of its band; the API takes band STARTS and
        // derives every bound from them, so rowsToSlabs does the conversion.
        ...(basis === "amount" ? { slabs: rowsToSlabs(rows) } : {}),
        // No auth middleware on the pawn routes — the acting user rides in the
        // body so the backend can stamp updated_by.
        user_id: userId,
      };
      const res = await axios.put(
        `${API_BASE_URL}/api/pawn/amount-slabs/${storeId}`,
        payload,
      );
      if (res.data.success) {
        const savedRows =
          basis === "amount" ? slabsToRows(res.data.data?.slabs) : saved.rows;
        setSaved({ basis, rows: savedRows });
        if (basis === "amount") setRows(savedRows);
        showSnackbar(
          res.data.statusMessage || "Interest basis saved",
          "success",
        );
      } else {
        showSnackbar(
          res.data.statusMessage || "Failed to save the interest basis",
          "error",
        );
      }
    } catch (error) {
      showSnackbar(
        error.response?.data?.statusMessage ||
          "Failed to save the interest basis",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setBasis(saved.basis);
    setRows(saved.rows);
    setRowErrors({});
  };

  const basisOptions = useMemo(
    () => [
      {
        value: "month",
        label: "Month-wise",
        icon: <CalendarMonthRoundedIcon sx={{ fontSize: 15 }} />,
      },
      {
        value: "amount",
        label: "Amount-wise",
        icon: <CurrencyRupeeRoundedIcon sx={{ fontSize: 15 }} />,
      },
    ],
    [],
  );

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        borderRadius: CARD_RADIUS,
        border: "1px solid #e8eaf0",
        boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
        // Fills its quadrant from lg up; below that the settings page stacks
        // its sections and scrolls, so the card stands at its natural height
        // with a floor under the table — a quarter of a phone screen is two
        // rows and a scrollbar.
        height: { xs: "auto", lg: "100%" },
        minHeight: { xs: 320, lg: 0 },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Header: title, then the basis switch ── */}
      <Box
        sx={{
          flexShrink: 0,
          px: 2,
          py: 1.4,
          borderBottom: "1px solid #f0f2f5",
          bgcolor: "#fafbfc",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              bgcolor: `${PURPLE}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: PURPLE,
              "& svg": { fontSize: 17 },
            }}
          >
            <PercentRoundedIcon />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#1a1a2e",
              fontSize: "0.88rem",
              letterSpacing: "0.1px",
            }}
          >
            Interest Slab
          </Typography>
          <Tooltip title="Know more" arrow>
            <IconButton
              size="small"
              onClick={() => setInfoOpen(true)}
              sx={{
                p: "2px",
                color: "#94a3b8",
                "&:hover": { color: PURPLE, bgcolor: `${PURPLE}12` },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* The switch, not a view tab: it decides how this store prices loans.
            Labelled so, right above it, because the two are one thought. */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: INK_MUTED,
              fontWeight: 600,
              display: { xs: "none", sm: "block" },
            }}
          >
            Charge by
          </Typography>
          <ToggleButtonGroup
            value={basis}
            exclusive
            size="small"
            onChange={handleBasisChange}
            sx={{
              "& .MuiToggleButton-root": {
                textTransform: "none",
                px: { xs: 1, sm: 1.4 },
                py: 0.4,
                fontSize: "0.74rem",
                fontWeight: 700,
                color: INK_MUTED,
                borderColor: BORDER,
                gap: 0.5,
                whiteSpace: "nowrap",
              },
              "& .Mui-selected": {
                bgcolor: `${PURPLE} !important`,
                color: "#fff !important",
                borderColor: `${PURPLE} !important`,
                "&:hover": { bgcolor: `${PURPLE} !important` },
              },
            }}
          >
            {basisOptions.map((opt) => (
              <ToggleButton key={opt.value} value={opt.value}>
                {opt.icon}
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* ── Body: whichever editor the basis calls for ── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          p: basis === "amount" ? 1 : 0,
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={26} sx={{ color: PURPLE }} />
          </Box>
        ) : basis === "month" ? (
          <PawnSchemeManagement
            storeId={storeId}
            currentStatus={currentStatus}
            embedded
            userId={userId}
          />
        ) : (
          <>
            {/* Scheme Master shows an "Int. 2 %" column for the unofficial book,
                so someone arriving here in unofficial mode will look for its
                counterpart. There isn't one, and that is deliberate. */}
            {Number(currentStatus) === 2 && (
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: AMBER_INK,
                  bgcolor: AMBER_BG,
                  border: `1px solid ${AMBER_LINE}`,
                  borderRadius: "8px",
                  px: 1,
                  py: 0.6,
                  mx: 1,
                  mb: 0.75,
                  flexShrink: 0,
                }}
              >
                One rate per band, charged in both the official and the
                unofficial book.
              </Typography>
            )}
            <PawnAmountSlabs
              rows={rows}
              onRowsChange={handleRowsChange}
              errors={rowErrors}
            />
          </>
        )}
      </Box>

      {/* ── Action bar: only present while something is unsaved ──
          Scoped to this section so there is no doubt about what it writes, and
          absent the rest of the time so the page shows one Save button. */}
      {dirty && !loading && (
        <Box
          sx={{
            flexShrink: 0,
            px: 1.5,
            py: 1,
            borderTop: `1px solid ${AMBER_LINE}`,
            bgcolor: AMBER_BG,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{ fontSize: "0.73rem", color: AMBER_INK, fontWeight: 600 }}
          >
            {basisChanged
              ? basis === "amount"
                ? "Not saved — new loans will be priced from the loan amount."
                : "Not saved — new loans will be priced from the scheme."
              : "Slabs not saved."}
          </Typography>
          <Box sx={{ display: "flex", gap: 0.75 }}>
            <Button
              size="small"
              onClick={handleDiscard}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.76rem",
                color: INK_MUTED,
                borderRadius: "8px",
              }}
            >
              Discard
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={
                saving ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <SaveRoundedIcon sx={{ fontSize: "15px !important" }} />
                )
              }
              sx={{
                bgcolor: PURPLE,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.76rem",
                borderRadius: "8px",
                px: 1.6,
                boxShadow: "none",
                whiteSpace: "nowrap",
                "&:hover": { bgcolor: "#7E22CE", boxShadow: "none" },
              }}
            >
              {saving ? "Saving…" : "Save interest slab"}
            </Button>
          </Box>
        </Box>
      )}

      {/* ── "Know more": the two bases ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontWeight: 700,
            fontSize: "1rem",
            color: INK,
            pb: 1,
          }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 20, color: PURPLE }} />
          {BASIS_INFO.title}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: "#374151", lineHeight: 1.65, fontSize: "0.88rem" }}
          >
            {BASIS_INFO.description}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              bgcolor: BLUE,
              boxShadow: "none",
              "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          sx={{ borderRadius: "10px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnInterestSlabs;
